import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
} from 'lucide-react';

export type ManualAlertTone = 'error' | 'warning' | 'success' | 'info';

export type ManualAlertAction = {
  label: string;
  onClick?: () => void;
  /** Primary filled button (defaults to last action) */
  primary?: boolean;
};

export type ManualAlertRequest = {
  tone?: ManualAlertTone;
  title: string;
  description?: string;
  items?: string[];
  actions?: ManualAlertAction[];
};

type ManualAlertApi = {
  show: (req: ManualAlertRequest) => void;
  error: (
    title: string,
    description?: string,
    extras?: Omit<ManualAlertRequest, 'tone' | 'title' | 'description'>,
  ) => void;
  warning: (
    title: string,
    description?: string,
    extras?: Omit<ManualAlertRequest, 'tone' | 'title' | 'description'>,
  ) => void;
  success: (
    title: string,
    description?: string,
    extras?: Omit<ManualAlertRequest, 'tone' | 'title' | 'description'>,
  ) => void;
  info: (
    title: string,
    description?: string,
    extras?: Omit<ManualAlertRequest, 'tone' | 'title' | 'description'>,
  ) => void;
  dismiss: () => void;
};

const ManualAlertContext = createContext<ManualAlertApi | null>(null);

const TONE_UI: Record<
  ManualAlertTone,
  { wrap: string; icon: typeof XCircle; confirm: string }
> = {
  error: {
    wrap: 'bg-rose-50 text-rose-600',
    icon: XCircle,
    confirm: 'bg-rose-600 text-white hover:bg-rose-700',
  },
  warning: {
    wrap: 'bg-amber-50 text-amber-700',
    icon: AlertTriangle,
    confirm: 'bg-amber-600 text-white hover:bg-amber-700',
  },
  success: {
    wrap: 'bg-emerald-50 text-emerald-700',
    icon: CheckCircle2,
    confirm: 'bg-emerald-600 text-white hover:bg-emerald-700',
  },
  info: {
    wrap: 'bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]',
    icon: Info,
    confirm: 'bg-[var(--dash-brand)] text-white hover:brightness-105',
  },
};

function ManualAlertDialog({
  open,
  tone,
  title,
  description,
  items,
  actions,
  onDismiss,
}: {
  open: boolean;
  tone: ManualAlertTone;
  title: string;
  description?: string;
  items?: string[];
  actions: ManualAlertAction[];
  onDismiss: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open || typeof document === 'undefined') return null;

  const ui = TONE_UI[tone];
  const Icon = ui.icon;
  const resolved = actions.length
    ? actions
    : [{ label: 'OK', primary: true } satisfies ManualAlertAction];

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 border-none bg-[#0F1B2D]/48 backdrop-blur-[3px]"
        aria-label="Dismiss"
        onClick={onDismiss}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="manual-alert-title"
        aria-describedby="manual-alert-desc"
        className="camp-fade-up relative z-10 w-full max-w-[420px] overflow-hidden rounded-t-3xl border border-[#E6E8EF] bg-white shadow-[0_24px_64px_-20px_rgba(15,27,45,0.45)] sm:rounded-2xl"
      >
        <div className="flex gap-3 px-5 pt-5 pb-1">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ui.wrap}`}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 text-left">
            <h2
              id="manual-alert-title"
              className="m-0 text-[16px] font-semibold tracking-tight text-[#1F2433]"
            >
              {title}
            </h2>
            {description ? (
              <p
                id="manual-alert-desc"
                className="mt-1.5 mb-0 text-[13px] leading-relaxed text-[#6B7289]"
              >
                {description}
              </p>
            ) : (
              <span id="manual-alert-desc" className="sr-only">
                {title}
              </span>
            )}
          </div>
        </div>

        {items && items.length > 0 ? (
          <ul className="mx-5 mt-3 mb-0 max-h-[36vh] list-none space-y-1.5 overflow-y-auto rounded-xl border border-[#EEF0F5] bg-[#F8F9FB] p-2">
            {items.map((item) => (
              <li
                key={item}
                className="rounded-lg bg-white px-2.5 py-2 text-[12.5px] font-medium text-[#1F2433]"
              >
                {item}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-4 flex flex-col-reverse gap-2 border-t border-[#EEF0F5] bg-[#F8F9FB] px-5 py-3.5 sm:flex-row sm:justify-end">
          {resolved.map((action, index) => {
            const isPrimary =
              action.primary ?? index === resolved.length - 1;
            return (
              <button
                key={`${action.label}-${index}`}
                type="button"
                autoFocus={isPrimary}
                onClick={() => {
                  action.onClick?.();
                  onDismiss();
                }}
                className={
                  isPrimary
                    ? `inline-flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-semibold transition ${ui.confirm}`
                    : 'inline-flex h-10 items-center justify-center rounded-xl border border-[#E6E8EF] bg-white px-4 text-[13px] font-semibold text-[#1F2433] transition hover:bg-[#F3F4F8]'
                }
              >
                {action.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ManualAlertProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ManualAlertRequest | null>(null);

  const dismiss = useCallback(() => setActive(null), []);

  const show = useCallback((req: ManualAlertRequest) => {
    setActive(req);
  }, []);

  const api = useMemo<ManualAlertApi>(
    () => ({
      show,
      dismiss,
      error: (title, description, extras) =>
        show({ tone: 'error', title, description, ...extras }),
      warning: (title, description, extras) =>
        show({ tone: 'warning', title, description, ...extras }),
      success: (title, description, extras) =>
        show({ tone: 'success', title, description, ...extras }),
      info: (title, description, extras) =>
        show({ tone: 'info', title, description, ...extras }),
    }),
    [show, dismiss],
  );

  const tone = active?.tone ?? 'info';
  const actions = active?.actions ?? [{ label: 'OK', primary: true }];

  return (
    <ManualAlertContext.Provider value={api}>
      {children}
      <ManualAlertDialog
        open={Boolean(active)}
        tone={tone}
        title={active?.title ?? ''}
        description={active?.description}
        items={active?.items}
        actions={actions}
        onDismiss={dismiss}
      />
    </ManualAlertContext.Provider>
  );
}

/** Required inside ManualAlertProvider (Manual campaign create). */
export function useManualAlert(): ManualAlertApi {
  const ctx = useContext(ManualAlertContext);
  if (!ctx) {
    throw new Error('useManualAlert must be used within ManualAlertProvider');
  }
  return ctx;
}

/** Safe outside provider — returns null so callers can fall back to toast. */
export function useManualAlertOptional(): ManualAlertApi | null {
  return useContext(ManualAlertContext);
}
